import { PRODUCT_LISTS_KEY, productListArrayToMap } from '.';
import { flattenList, normalizeServerList } from './list-transforms';
import uniqueId from '../../helpers/uniqueId';
export function loginAction({ loginMutation, fetchAll }) {
    return (dispatch, getState) => {
        const state = getState();
        // Merge what is in the default list onto the default list on the server
        // for this user.
        const list = state.lists.get(null);
        loginMutation({
            fetchPolicy: 'no-cache',
            context: { useApolloNetworkStatus: false },
            variables: {
                id: null,
                items: flattenList(list).map((listItem) => ({
                    articleNumber: listItem.articleNumber,
                    quantity: listItem.options.quantity,
                    description: listItem.options.description
                }))
            }
        })
            .then((response) => {
            const list = normalizeServerList(response.data.addToCustomerProductList.customerProductList);
            dispatch({
                type: 'LOGIN',
                payload: {
                    listId: null,
                    list
                }
            });
            // Also fetch any other list that this user might have and update the state.
            return fetchAll().then((response) => {
                response.data.customerProductLists.forEach((serverList) => {
                    if (serverList.name === null) {
                        // The null (default) list has already been fetched and added as listId null
                        // so ignore it here.
                        return;
                    }
                    const list = normalizeServerList(serverList);
                    dispatch({
                        type: 'REPLACE',
                        payload: {
                            listId: serverList.id,
                            list
                        }
                    });
                });
            });
        })
            .catch((err) => {
            console.error('Lists could not be merged', err);
        });
    };
}
export function refreshAction() {
    return (dispatch) => {
        const lists = JSON.parse(localStorage.getItem(PRODUCT_LISTS_KEY));
        if (lists) {
            dispatch({
                type: 'SET_LISTS',
                payload: {
                    lists: productListArrayToMap(lists)
                }
            });
        }
    };
}
export function createListAction({ createMutation, name }) {
    const variables = {
        input: {
            name
        }
    };
    return (dispatch) => {
        createMutation({ variables }).then((response) => {
            const serverList = response.data.createCustomerProductList.customerProductList;
            const listId = serverList.id;
            if (response.data.createCustomerProductList.success) {
                dispatch({
                    type: 'CREATE_LIST',
                    payload: { listId, list: normalizeServerList(serverList) }
                });
            }
            else {
                console.warn(`Could not create list`);
            }
        });
    };
}
export function deleteListAction({ listId, deleteListMutation }) {
    const variables = {
        id: listId
    };
    return (dispatch) => {
        deleteListMutation({ variables }).then((response) => {
            const success = response.data.deleteCustomerProductList.success;
            if (success) {
                dispatch({
                    type: 'DELETE_LIST',
                    payload: { listId }
                });
            }
            else {
                console.warn(`Could not delete product list with id ${listId}`);
            }
        });
    };
}
export function updateAction({ listId, updateAPI = false, removeMutation, addMutation, options, articleNumber, variantToReplace, variantArticleNumber }) {
    return (dispatch, getState) => {
        const { description, quantity } = options;
        dispatch({
            type: 'UPDATE',
            payload: {
                listId,
                articleNumber,
                options,
                variantArticleNumber,
                variantToReplace
            }
        });
        if (!updateAPI)
            return;
        const [remove] = removeMutation;
        const [add] = addMutation;
        const previousState = getState();
        const requestId = scopedUniqueID();
        previousState.requestIdRef.current = requestId;
        remove({
            // Don't write to the cache. This avoids re-rendering the list.
            fetchPolicy: 'no-cache',
            context: { useApolloNetworkStatus: false },
            variables: {
                id: listId,
                articleNumbers: [variantToReplace || articleNumber]
            }
        })
            .then(() => {
            add({
                fetchPolicy: 'no-cache',
                context: { useApolloNetworkStatus: false },
                variables: {
                    id: listId,
                    items: [
                        {
                            articleNumber: variantArticleNumber || articleNumber,
                            quantity,
                            description
                        }
                    ]
                }
            }).then((response) => {
                requestId === previousState.requestIdRef.current &&
                    syncListFromServer(dispatch, listId, response.data.addToCustomerProductList);
            });
        })
            .catch((err) => {
            console.error('List could not be updated', err);
            const previousList = previousState.lists.get(listId);
            if (previousList) {
                rollbackList(dispatch, listId, previousList);
            }
        });
    };
}
export function removeAction({ listId, updateAPI = false, articleNumber, variantArticleNumber, removeMutation }) {
    return (dispatch, getState) => {
        dispatch({
            type: 'REMOVE',
            payload: {
                listId,
                articleNumber,
                variantArticleNumber
            }
        });
        if (!updateAPI)
            return;
        const [remove] = removeMutation;
        const previousState = getState();
        const requestId = scopedUniqueID();
        previousState.requestIdRef.current = requestId;
        remove({
            fetchPolicy: 'no-cache',
            context: { useApolloNetworkStatus: false },
            variables: {
                articleNumbers: [variantArticleNumber || articleNumber],
                id: listId
            }
        })
            .then((response) => {
            requestId === previousState.requestIdRef.current &&
                syncListFromServer(dispatch, listId, response.data.removeFromCustomerProductList);
        })
            .catch((err) => {
            console.error('Product could not be removed from list', err);
            const previousList = previousState.lists.get(listId);
            if (previousList) {
                rollbackList(dispatch, listId, previousList);
            }
        });
    };
}
export function clearAction({ listId, updateAPI = false, removeMutation }) {
    return (dispatch, getState) => {
        dispatch({
            type: 'CLEAR_ITEMS',
            payload: { listId }
        });
        if (!updateAPI)
            return;
        const [remove] = removeMutation;
        const state = getState();
        const list = state.lists.get(listId);
        const articleNumbers = flattenList(list).map((listItem) => listItem.articleNumber);
        const requestId = scopedUniqueID();
        state.requestIdRef.current = requestId;
        remove({
            fetchPolicy: 'no-cache',
            context: { useApolloNetworkStatus: false },
            variables: { articleNumbers, id: listId }
        })
            .then((response) => {
            requestId === state.requestIdRef.current &&
                syncListFromServer(dispatch, listId, response.data.removeFromCustomerProductList);
        })
            .catch((err) => {
            console.error('List could not be cleared', err);
            rollbackList(dispatch, listId, list);
        });
    };
}
export function addAction({ listId, articleNumber, variantArticleNumber, addMutation, options, updateAPI = false }) {
    return (dispatch, getState) => {
        const { quantity, description } = options;
        dispatch({
            type: 'ADD',
            payload: {
                listId,
                articleNumber,
                variantArticleNumber,
                options: { quantity, description }
            }
        });
        if (!updateAPI)
            return;
        const [add] = addMutation;
        const previousState = getState();
        const requestId = scopedUniqueID();
        previousState.requestIdRef.current = requestId;
        add({
            fetchPolicy: 'no-cache',
            context: { useApolloNetworkStatus: false },
            variables: {
                //id: null,  XXX
                id: listId,
                items: [
                    {
                        articleNumber: variantArticleNumber || articleNumber,
                        quantity,
                        description
                    }
                ]
            }
        })
            .then(({ data: { addToCustomerProductList } }) => {
            requestId === previousState.requestIdRef.current &&
                syncListFromServer(dispatch, listId, addToCustomerProductList);
        })
            .catch((err) => {
            console.error('Product could not be added to list', err);
            rollbackList(dispatch, listId, previousState.lists.get(listId));
        });
    };
}
function scopedUniqueID() {
    return uniqueId('_product_list_');
}
function syncListFromServer(dispatch, listId, productList) {
    const list = normalizeServerList(productList.customerProductList);
    dispatch({
        type: 'REPLACE',
        payload: {
            listId,
            list
        }
    });
}
function rollbackList(dispatch, listId, productList) {
    dispatch({
        type: 'REPLACE',
        payload: {
            listId,
            list: productList
        }
    });
}
//# sourceMappingURL=action-creators.js.map